import { type FragmentType, useFragment, graphql } from "@repo/graphql-types";
import { cn } from "@repo/lib";
import { MilkShake, PartyConfetti } from "@repo/icons";
import { Link } from "@tanstack/react-router";
import { getTotalMinutes } from "@/lib/time.ts";
import { WaitingRoomAppointmentListItem } from "@/components/waiting-room-appointment-list-item.tsx";
import {
  CarouselContainer,
  CarouselContent,
  CarouselItem,
  CarouselRoot,
} from "@/components/carousel.tsx";
import { Button } from "@/components/button.tsx";
import {
  EmptyStateV2Content,
  EmptyStateV2Description,
  EmptyStateV2Icon,
  EmptyStateV2Root,
} from "@/components/empty-state-v2.tsx";
import { HomeAppointmentAlert } from "@/components/home/home-alert-change-room.tsx";

export const HomeNextSchedulesCarouselQueryFragment = graphql(/* GraphQL */ `
  fragment HomeNextSchedulesCarouselQueryFragment on query_root {
    agendamentos(
      where: {
        data: { _eq: $today }
        cancelado: { _eq: false }
        codFinalidadeAgendamento: { _nin: [2, 4] }
      }
      order_by: { horaInicio: asc }
    ) {
      codAgendamento
      horaFim
      horaInicio
      agendamentoPreAlocacao: AgendamentoPreAlocacao {
        UnidadeSala {
          TipoSala {
            nome
            codTipoSala
          }
        }
      }
      agendamentoAlocacaoAtiva: AgendamentosAlocacoesAtivas {
        UnidadeSala {
          TipoSala {
            nome
            codTipoSala
          }
        }
      }
      compromisso: UsuarioCompromisso {
        UsuariosCompromissosTiposSalas {
          codTipoSala
          codUsuarioCompromisso
          prioridade
        }
      }
      ...WaitingRoomAppointmentListItemFragment
    }
  }
`);

interface HomeNextSchedulesCarouselProps {
  data: FragmentType<typeof HomeNextSchedulesCarouselQueryFragment>;
}

export const HomeNextSchedulesCarousel = ({
  data,
}: HomeNextSchedulesCarouselProps): JSX.Element => {
  const appointments = useFragment(HomeNextSchedulesCarouselQueryFragment, data);

  const now = new Date();
  const nowInMinutes = getTotalMinutes({
    hour: now.getHours(),
    minute: now.getMinutes(),
    second: 0,
  });

  const filteredAppointments = appointments.agendamentos.filter((appointment) => {
    const appointmentStartInMinutes = getTotalMinutes(appointment.horaInicio);

    return (
      appointmentStartInMinutes > nowInMinutes ||
      appointment.agendamentoAlocacaoAtiva.length > 0
    );
  });

  if (filteredAppointments.length === 0) {
    const anyAppointmentToday = appointments.agendamentos.length > 0;

    const emptyStateText = anyAppointmentToday
      ? "Boa! Você completou todos os seus agendamentos hoje."
      : "Você não tem nenhum atendimento agendado para hoje.";

    const emptyStateIcon = anyAppointmentToday ? PartyConfetti : MilkShake;

    return (
      <EmptyStateV2Root className="items-start bg-white mx-4 p-4 rounded-40">
        <EmptyStateV2Content>
          <EmptyStateV2Icon
            className={cn({
              "bg-neutral-50 p-2 stroke-0 ": true,
              "fill-neutral-400": !anyAppointmentToday,
              "fill-black": anyAppointmentToday,
            })}
            Icon={emptyStateIcon}
          />
          <EmptyStateV2Description className="text-black font-medium text-sm">
            {emptyStateText}
          </EmptyStateV2Description>
        </EmptyStateV2Content>
        {!anyAppointmentToday ? (
          <Link to="/appointment/create" id="create-appointment-home-button-empty-state">
            <Button
              className="px-3 min-h-0 w-fit text-xs"
              type="button"
              rounded
              size="small"
            >
              Agende uma consulta
            </Button>
          </Link>
        ) : null}
      </EmptyStateV2Root>
    );
  }

  if (filteredAppointments.length === 1) {
    return (
      <div className="px-4">
        <WaitingRoomAppointmentListItem data={filteredAppointments[0]} />
      </div>
    );
  }

  return (
    <CarouselRoot align="center">
      <CarouselContainer>
        <CarouselContent className="m-[1px]">
          {filteredAppointments.map((item, index) => (
            <>
              <CarouselItem
                className={cn({
                  "mr-4": index === filteredAppointments.length - 1,
                  "ml-4": index === 0,
                })}
                key={`${item.codAgendamento}-item`}
              >
                <WaitingRoomAppointmentListItem data={item} />
              </CarouselItem>
              <HomeAppointmentAlert
                key={`${item.codAgendamento}-alert`}
                appointments={filteredAppointments}
                index={index}
              />
            </>
          ))}
        </CarouselContent>
      </CarouselContainer>
    </CarouselRoot>
  );
};
